import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyPaths } from '../../../hooks/useSurveyPaths';
import { Link } from 'react-router-dom';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { FileTextIcon } from '@navikt/aksel-icons';
import process from '../WorkEnvironmentSurveyProcess.module.css';

type Report = 'environment' | 'inspection';

const TEXTS: Record<Report, { title: string; text: string; open: string }> = {
  environment: {
    title: 'workEnvironmentReport.card.title',
    text: 'workEnvironmentReport.card.text',
    open: 'workEnvironmentReport.card.open',
  },
  inspection: {
    title: 'workEnvironmentInspection.card.title',
    text: 'workEnvironmentInspection.card.text',
    open: 'workEnvironmentInspection.card.open',
  },
};

/** A link to the work environment report, or to the inspection report of the team */
export const ReportCard = ({
  surveyId,
  report = 'environment',
}: {
  surveyId: number;
  report?: Report;
}): ReactElement => {
  const { t } = useTranslation();
  const paths = useSurveyPaths();
  const texts = TEXTS[report];
  const to = report === 'environment' ? paths.report(surveyId) : paths.inspectionReport(surveyId);
  return (
    <section
      aria-labelledby={`${report}-report-heading`}
      className={`${process.card} ${process.sideCard}`}
      data-gap="sm"
    >
      <Heading level={2} data-size="xs" id={`${report}-report-heading`}>
        {t(texts.title)}
      </Heading>
      <Paragraph data-size="sm" className={process.subtle}>
        {t(texts.text)}
      </Paragraph>
      <Button asChild variant="secondary" data-size="sm">
        <Link to={to}>
          <FileTextIcon aria-hidden />
          {t(texts.open)}
        </Link>
      </Button>
    </section>
  );
};
